import ComingSoonIcon from '@image/icons/png-icons/ComingSoonIcon.png';
import cn from 'clsx';
import Link from 'next/link';
import Image from 'next/image';
import {priceHelper, returnHighestAndLowestPriceValues} from '@helpers/config-master.mjs';
import {
  FreqBoughtTogetherAddToCartButton
} from '@components/product/global/FreqBoughtTogetherAddToCart';
import {buildUrlWithQueryParams} from '@helpers/common/fetch.mjs';
import {HStack} from '@components/widgets/Common';
import {formatPriceNumeric} from '@components/product/global/formatPriceNumeric';
import {niceMoney} from '@helpers/common/number.mjs';
import {useSession} from 'next-auth/react';
import {ReviewStarsFromDb} from '@components/product/reviews/review-stars-from-db';

const placeholderImg = priceHelper.placeholder.desktop.product;

export const FrequentlyBoughtTogether = ({products, showTitle = true}) => {
  // console.log('[FrequentlyBoughtTogether][Products]:', products);
  const {data: session} = useSession();
  const isLoggedIn = session && !!session?.user?.email;
  const isDealer = isLoggedIn && session?.user?.role === 2;

  const hasFreqBoughtTogProducts = products?.length > 0;
  if (!hasFreqBoughtTogProducts) return;

  return (
    <>
      <div className='mt-2 mb-2'>
        {showTitle && (
          <div className='border-b mb-3 md:mb-4'>
            <h2
              role='heading'
              aria-level='2'
              className='font-oswald text-xl md:text-3xl uppercase pb-2 text-center'>
              Frequently Bought Together
            </h2>
          </div>
        )}

        <div role='list' className='flex flex-col md:flex-row gap-1 md:gap-8 w-full'>
          {products?.map((product) => {
            return (
              <div
                role='listitem'
                key={`fbt-${product?.slug}`}
                className={
                  'flex-1 mx-2 mb-4 pb-4 md:m-0 md:p-0 border-b-2 last:border-none last:mb-0 last:pb-0 md:border-b-0'
                }>
                <FrequentlyBoughtTogetherCard product={product} isDealer={isDealer} />
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

export const FrequentlyBoughtTogetherCard = ({product, isDealer, fontSize = 17}) => {
  // console.log('[FrequentlyBoughtTogetherCard][Product]:', product);
  const [highPrice, lowPrice] = returnHighestAndLowestPriceValues(product, {isDealer});

  let src = product?.imageUrl ?? product?.image?.url ?? product?.images?.[0]?.url ?? placeholderImg;
  const presetAddToCartFbtQty = product?.quantity ?? 1;

  let isInStock = product?.currentlyInStock;
  let isOutOfStock = !isInStock;
  if (isOutOfStock) return null;

  const price = formatPriceNumeric(lowPrice);
  const compareAtPrice = formatPriceNumeric(highPrice);

  let productHref = `/products/${product?.slug}`;
  if (product?.is_variant_sku === true) {
    productHref = buildUrlWithQueryParams(productHref, {variant: product?.sku});
  }

  return (
    <div className='flex gap-2 md:flex-col h-full' role='group'>
      <div className='w-3/12 md:w-full -mb-1'>
        <div className={'border border-1 rounded box-content bg-clip-content overflow-hidden'}>
          {src && (
            <div className='relative'>
              <Link href={productHref} aria-label={`${product?.name}`}>
                <Image
                  alt={product?.name}
                  className='rounded'
                  src={src}
                  height={225}
                  width={225}
                  quality='80'
                  loading='eager'
                  sizes={`225px`}
                />

                {src === placeholderImg && (
                  <div className='absolute top-0 p-16'>
                    <Image src={ComingSoonIcon} alt='Placeholder, image coming soon' />
                  </div>
                )}
              </Link>
            </div>
          )}
        </div>
      </div>
      <div className='w-9/12 md:w-full md:h-full flex flex-col'>
        <div className={'md:flex-1'}>
          <div className='md:mt-1 md:text-center'>
            <Link
              href={productHref}
              aria-label={`Link to ${product?.name}`}
              style={{
                fontSize: `${fontSize}px`,
                lineHeight: `${fontSize - 2}px`
              }}>
              {product?.name}
            </Link>
          </div>

          <div className='flex md:flex-col gap-2 md:gap-0'>
            <div className='flex md:justify-center mt-1 md:mt-3 md:mb-2'>
              {product?.rating && (
                <ReviewStarsFromDb
                  avgScore={product?.rating?.avg_rating}
                  count={product?.rating?.count}
                  size={14}
                />
              )}
            </div>

            <div className={'md:hidden'}>
              <FreqBoughtTogPricingInCard compareAtPrice={compareAtPrice} price={price} />
            </div>
          </div>
        </div>
        <div className={'hidden md:block'}>
          <FreqBoughtTogPricingInCard compareAtPrice={compareAtPrice} price={price} />
        </div>
        <div className='flex justify-end md:justify-center'>
          <FreqBoughtTogetherAddToCartButton
            className={
              'block m-0 items-center justify-center uppercase md:font-medium text-center' +
              ' text-sm tracking-wide px-8 py-2 md:py-2 rounded disabled:border-gray-300' +
              ' disabled:bg-gray-300 mt-4 disabled:cursor-not-allowed text-white bg-neutral-800' +
              ' hover:bg-neutral-700 active:bg-neutral-800 transition ease-in-out duration-150'
            }
            quantity={presetAddToCartFbtQty}
            selectedVariant={product}
            iconSize={16}
            addToCartButtonSource={'FreqBoughtTogether'}
            aria-label={`Add ${product?.name} to cart`}>
            Add To Cart
          </FreqBoughtTogetherAddToCartButton>
        </div>
      </div>
    </div>
  );
};

function FreqBoughtTogPricingInCard({compareAtPrice, price}) {
  return (
    <HStack className={cn('justify-start md:justify-center')} role='presentation'>
      <div
        className={cn(
          'px-1 md:text-lg text-accent-9 tracking-wide',
          'text-right line-through decoration-red-500 decoration-solid ' +
            'decoration-green-500 decoration-2'
        )}
        style={{textDecorationColor: 'red'}}>
        {niceMoney(compareAtPrice)}
      </div>
      <div
        className={cn(
          'px-1 text-accent-9 font-semibold tracking-wide text-right text-sky-700 font-oswald md:text-xl'
        )}>
        {niceMoney(price)}
      </div>
    </HStack>
  );
}
